import React, { Fragment, useCallback, useContext, useEffect, useState } from 'react';
import { EditorContext, SiteContext } from '../../../utils/contexts';
import CollapsedPanel from '../components/CollapsedPanel';
import Switcher from '../components/Switcher';
import { Label, LabelSelector } from '../Steps/components';
import NumberInput from '../components/NumberInput';
import InputBlock from '../components/InputBlock';
import SelectInput from '../components/SelectInput';
import ColorPicker from '../components/ColorPicker';
import Dropzone from '../components/Dropzone';
import useAPI from '../../../utils/api';
import { findTheme } from '../ThemeSelector/updateTheme';
import styled from 'styled-components';
import CloseIcon from '@mui/icons-material/Close';
import ConfirmationDialog from '../../Modals/ConfirmationDialog';
import { TextField } from '@mui/material';
import { useSnackbar } from 'notistack';

const LibraryImageButton = styled.div`
  transition: 0.5s ease;
  opacity: 0;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  -ms-transform: translate(-50%, -50%);
  text-align: center;

  button {
    background-color: #e9562b;
    color: white;
    font-size: 1em;
    padding: 5px 10px;
    border-radius: 5px;
    cursor: pointer;
    border: none;
  }
  .delete-button {
    padding: 5px 6px !important;
    margin-left: 5px;
    svg {
      width: 13px;
      height: auto;
    }
  }
`;

const LibraryImageWrapper = styled.div`
  opacity: 1;
  display: block;
  width: 100%;
  height: auto;
  transition: 0.5s ease;
  backface-visibility: hidden;
  position: relative;
  margin: 3px;

  &:hover * {
    opacity: 1;
  }

  &:hover img {
    opacity: 0.3;
  }
`;

const LibraryImage = ({ image, onSelect, onDelete }) => {
  const onClick = () => {
    onSelect(image);
  };

  const deleteImage = () => {
    onDelete(image.id);
  };

  const deleteConfirmation = ({ onClick }) => (
    <button title="Delete Image" className="delete-button" onClick={onClick}>
      <CloseIcon />
    </button>
  );

  return (
    <LibraryImageWrapper>
      <img src={image.modal_url} alt="" />
      <LibraryImageButton>
        <button onClick={onClick}>Use it</button>
        <ConfirmationDialog
          onConfirm={() => deleteImage()}
          component={deleteConfirmation}
        >
          Are you sure you want to delete this image? <br />
          The image might be used in one of your popups.
        </ConfirmationDialog>
      </LibraryImageButton>
    </LibraryImageWrapper>
  );
};

const FixedCollapsedPanel = styled(CollapsedPanel)`
  height: 200px;
  overflow-y: auto;
`;

function LibrarySection({ loadLibrary, images, onSelect, onDelete }) {
  useEffect(() => { loadLibrary(); }, [loadLibrary]);

  return (
    <FixedCollapsedPanel open={true} title="Your Library">
      {images.map((image) => (
        <LibraryImage
          key={image.id.toString()}
          onSelect={onSelect}
          onDelete={() => onDelete(image.id)}
          image={image}
        />
      ))}
    </FixedCollapsedPanel>
  );
}

export default function ImagePanel() {
  const { settings, updateSettings, siteSettings, siteId, registerFlush } =
    useContext(EditorContext);
  const site = useContext(SiteContext);
  const theme = findTheme(settings, siteSettings);
  const defaultImage = theme && theme.image?.default_url;
  const API = useAPI();
  const { enqueueSnackbar } = useSnackbar();
  const [imageLoading, setImageLoading] = useState(false);
  const [popupImages, setPopupImages] = useState(
    (siteSettings.image_uploads || []).filter((img) => !img.image_type || img.image_type === 'popup')
  );
  const [libraryLoaded, setLibraryLoaded] = useState(false);
  const [videoUrlInput, setVideoUrlInput] = useState(settings.video_url || '');
  const [videoEmbedInput, setVideoEmbedInput] = useState(settings.video_embed_url || '');

  useEffect(() => {
    setVideoUrlInput(settings.video_url || '');
  }, [settings.video_url]);

  useEffect(() => {
    setVideoEmbedInput(settings.video_embed_url || '');
  }, [settings.video_embed_url]);

  const toggleDefaultImage = () => {
    const update = { use_default_image: !settings.use_default_image };
    if (update.use_default_image && defaultImage) {
      update.active_image_id = null;
      update.image_url = defaultImage;
      update.image_large_url = defaultImage;
      update.image_modal_url = defaultImage;
      update.image_file_name = null;
      update.image_placement = theme?.image?.position_default;
    } else if (!update.use_default_image) {
      update.image_url = null;
      update.image_large_url = null;
      update.image_modal_url = null;
      update.image_file_name = null;
    }
    updateSettings(update);
  };

  const updateOpacity = (opacity) => {
    updateSettings({ image_opacity: opacity });
  };

  const updateImagePlacement = (placement) => {
    updateSettings({ image_placement: placement });
  };

  const updateOverlayColor = (color) => {
    updateSettings({ image_overlay_color: color });
  };

  const updateOverlayOpacity = (opacity) => {
    updateSettings({ image_overlay_opacity: opacity });
  };

  const updateImagePositionX = (px) => {
    updateSettings({ image_position_x: px });
  };

  const updateImagePositionY = (px) => {
    updateSettings({ image_position_y: px });
  };

  const updateImageWidth = (val) => {
    updateSettings({ image_width: val });
  };

  const updateImageHeight = (val) => {
    updateSettings({ image_height: val });
  };

  const updateMediaType = (mediaType) => {
    const update = { media_type: mediaType };
    if (mediaType === 'image') {
      update.video_url = null;
      update.video_large_url = null;
      update.video_embed_url = null;
      update.video_source_type = 'upload';
    } else {
      // switching to video explicitly mark media_type and clear image fields
      update.active_image_id = null;
      update.image_file_name = null;
      update.image_url = null;
      update.image_large_url = null;
      update.image_modal_url = null;
      update.use_default_image = false;
      update.video_source_type = update.video_source_type || 'url';
    }
    updateSettings(update);
  };

  const updateVideoSourceType = (videoSourceType) => {
    updateSettings({
      media_type: 'video',
      video_source_type: videoSourceType,
      video_url: null,
      video_large_url: null,
      video_embed_url: null,
    });
    setVideoUrlInput('');
    setVideoEmbedInput('');
  };

  const saveVideoUrl = () => {
    const value = (videoUrlInput || '').trim();
    const embed = toEmbedUrl(value);
    if (embed) {
      updateSettings({
        media_type: 'video',
        video_source_type: 'embed',
        video_embed_url: value || null,
        video_url: null,
        video_large_url: null,
      });
      setVideoEmbedInput(value);
      return;
    }
    updateSettings({
      media_type: 'video',
      video_source_type: 'url',
      video_url: value || null,
      video_large_url: value || null,
      video_embed_url: null,
    });
  };

  const saveEmbedUrl = () => {
    const value = (videoEmbedInput || '').trim();
    const normalized = toEmbedUrl(value) || value;
    updateSettings({
      media_type: 'video',
      video_source_type: 'embed',
      video_embed_url: normalized || null,
      video_url: null,
      video_large_url: null,
    });
    setVideoEmbedInput(normalized || '');
  };

  const updateVideoUrlInput = (value) => {
    // Only update local input state while typing — defer syncing to settings until blur/save
    setVideoUrlInput(value);
  };

  const updateVideoEmbedInput = (value) => {
    // Only update local input state while typing — defer syncing to settings until blur/save
    setVideoEmbedInput(value);
  };

  // Returns the partial settings to commit for pending video inputs,
  // AND dispatches to keep local reducer in sync.
  // Returning the partial means TopBarNavigation doesn't need to wait
  // for React to apply the dispatch before reading settings for the POST.
  const flushPendingVideo = useCallback(() => {
    const currentMediaType = settings.media_type || 'image';
    if (currentMediaType !== 'video') return null;

    const sourceType =
      (settings.video_source_type || 'url') === 'upload'
        ? 'url'
        : settings.video_source_type || 'url';

    if (sourceType === 'embed') {
      const trimmed = (videoEmbedInput || '').trim();
      if (trimmed === (settings.video_embed_url || '')) return null;
      const normalized = toEmbedUrl(trimmed) || trimmed;
      const partial = {
        media_type: 'video',
        video_source_type: 'embed',
        video_embed_url: normalized || null,
        video_url: null,
        video_large_url: null,
      };
      // Also dispatch so local reducer stays in sync
      updateSettings(partial);
      return partial;
    } else {
      const trimmed = (videoUrlInput || '').trim();
      if (trimmed === (settings.video_url || '')) return null;
      const embedUrl = toEmbedUrl(trimmed);
      const partial = embedUrl
        ? {
            media_type: 'video',
            video_source_type: 'embed',
            video_embed_url: trimmed,
            video_url: null,
            video_large_url: null,
          }
        : {
            media_type: 'video',
            video_source_type: 'url',
            video_url: trimmed || null,
            video_large_url: trimmed || null,
            video_embed_url: null,
          };
      updateSettings(partial);
      return partial;
    }
  }, [
    videoEmbedInput,
    videoUrlInput,
    settings.media_type,
    settings.video_source_type,
    settings.video_embed_url,
    settings.video_url,
  ]);

  useEffect(() => {
    if (!registerFlush) return;
    const unregister = registerFlush(flushPendingVideo);
    return unregister;
  }, [registerFlush, flushPendingVideo]);

  const updateVideoFlag = (key, value) => {
    updateSettings({ [key]: value });
  };

  const toEmbedUrl = (rawUrl) => {
    if (!rawUrl) return null;
    const iframeMatch = rawUrl.match(/<iframe[^>]*\ssrc=["']([^"']+)["'][^>]*>/i);
    const normalizedUrl = iframeMatch?.[1] || rawUrl;
    try {
      const parsed = new URL(normalizedUrl);
      const host = parsed.hostname.toLowerCase();
      if (host.includes('youtube.com') || host.includes('youtu.be') || host.includes('vimeo.com')) {
        return normalizedUrl;
      }
      return null;
    } catch (_) {
      return null;
    }
  };

  const loadLibrary = useCallback(() => {
    if (libraryLoaded) return;
    API.getImageLibrary('popup').then(([ok, data]) => {
      if (ok) setPopupImages(data.image_uploads || []);
      setLibraryLoaded(true);
    });
  }, [libraryLoaded]);

  const updateImage = (_, file) => {
    setImageLoading(true);

    API.uploadImage(siteId, file, 'popup')
      .then(([ok, json]) => {
        setImageLoading(false);
        if (!ok) {
          enqueueSnackbar(json?.error || json?.message || 'Failed to upload image.', { variant: 'error' });
          return;
        }
        setPopupImages((prev) => [...prev, json]);
        updateSettings({
          active_image_id: json.id,
          image_file_name: json.image_file_name,
          image_url: json.url,
          image_large_url: json.large_url,
          image_modal_url: json.modal_url,
        });
      })
      .catch(() => {
        setImageLoading(false);
        enqueueSnackbar('Failed to upload image.', { variant: 'error' });
      });
  };

  const deleteImage = async (id) => {
    setImageLoading(true);

    await API.deleteImage(id)
      .then(([ok]) => {
        if (ok) {
          setPopupImages((prev) => prev.filter((img) => Number(img.id) !== Number(id)));
          setImageLoading(false);
          unSelectImage();
        }
      })
      .catch(() => setImageLoading(false));
  };

  const unSelectImage = () => {
    updateSettings({
      active_image_id: null,
      image_modal_url: null,
      image_large_url: null,
      image_url: null,
    });
  };

  const placements = [
    { value: 'position-default', label: 'Default' },
    { value: 'position-top', label: 'Top' },
    { value: 'position-left', label: 'Left' },
    { value: 'position-right', label: 'Right' },
    { value: 'position-background', label: 'Background' },
  ];

  const selectImage = (image) => {
    updateSettings({
      active_image_id: image.id,
      image_file_name: image.image_file_name,
      image_url: image.url,
      image_large_url: image.large_url,
      image_modal_url: image.modal_url,
    });
  };

  const imagePlacement =
    placements.find((p) => settings.image_placement === p.value)?.value ||
    'position-default';
  const effectivePlacement =
    imagePlacement === 'position-default' && settings.theme?.image?.position_default
      ? settings.theme.image.position_default
      : imagePlacement;
  const isBackgroundPlacement = effectivePlacement === 'position-background';
  const isSidePlacement = effectivePlacement === 'position-left' || effectivePlacement === 'position-right';
  const hasImagePlacement =
    window.hellobarSiteSettings.modules_filename !== 'modules.js';
  const isForms = settings?.element_subtype === 'forms' || settings?.subtype === 'forms';
  const mediaType = settings.media_type || 'image';
  const isImageMedia = mediaType === 'image';
  const showImageWidth = isImageMedia && !isBackgroundPlacement && !isSidePlacement;
  const showImageHeight = isImageMedia && !isBackgroundPlacement;
  const videoSourceTypeRaw = settings.video_source_type || 'url';
  // Back-compat: older records used "upload" but UI no longer shows it.
  const videoSourceType = videoSourceTypeRaw === 'upload' ? 'url' : videoSourceTypeRaw;
  const mediaTypes = [
    { value: 'image', label: 'Image' },
    { value: 'video', label: 'Video' },
  ];
  const videoSources = [
    { value: 'url', label: 'Direct video URL (.mp4/.webm)' },
    { value: 'embed', label: 'Embed URL (YouTube/Vimeo)' },
  ];

  return (
    <CollapsedPanel open={false} title="Media">
      <LabelSelector>
        <span>Media type</span>
        <SelectInput
          items={mediaTypes}
          value={mediaType}
          onChange={updateMediaType}
        />
      </LabelSelector>
      {isImageMedia && defaultImage && (
        <Label>
          <span>Use default image</span>
          <Switcher
            checked={settings.use_default_image}
            onChange={toggleDefaultImage}
          />
        </Label>
      )}
      {isImageMedia && (!settings.use_default_image || isForms) && (
        <Dropzone
          settings={settings}
          loading={imageLoading}
          onDelete={unSelectImage}
          onUpdate={updateImage}
        />
      )}
      {isImageMedia && (!settings.use_default_image || isForms) && (
        <LibrarySection loadLibrary={loadLibrary} images={popupImages} onSelect={selectImage} onDelete={deleteImage} />
      )}
      {!isImageMedia && (
        <Fragment>
          <LabelSelector>
            <span>Video source</span>
            <SelectInput
              items={videoSources}
              value={videoSourceType}
              onChange={updateVideoSourceType}
            />
          </LabelSelector>
          {videoSourceType !== 'embed' && (
            <InputBlock label="Video URL">
              <TextField
                value={videoUrlInput}
                onChange={(event) => updateVideoUrlInput(event.target.value)}
                onBlur={saveVideoUrl}
                size="small"
                placeholder="https://cdn.example.com/video.mp4"
              />
            </InputBlock>
          )}
          {videoSourceType === 'embed' && (
            <InputBlock label="Embed URL">
              <TextField
                value={videoEmbedInput}
                onChange={(event) => updateVideoEmbedInput(event.target.value)}
                onBlur={saveEmbedUrl}
                size="small"
                placeholder="https://www.youtube.com/watch?v=..."
              />
            </InputBlock>
          )}
          <Label>
            <span>Autoplay</span>
            <Switcher
              checked={!!settings.video_autoplay}
              onChange={() => updateVideoFlag('video_autoplay', !settings.video_autoplay)}
            />
          </Label>
          <Label>
            <span>Muted</span>
            <Switcher
              checked={settings.video_muted !== false}
              onChange={() => updateVideoFlag('video_muted', settings.video_muted === false)}
            />
          </Label>
          <Label>
            <span>Loop</span>
            <Switcher
              checked={!!settings.video_loop}
              onChange={() => updateVideoFlag('video_loop', !settings.video_loop)}
            />
          </Label>
          <Label>
            <span>Controls</span>
            <Switcher
              checked={settings.video_controls !== false}
              onChange={() => updateVideoFlag('video_controls', settings.video_controls === false)}
            />
          </Label>
        </Fragment>
      )}
      {hasImagePlacement ? (
        <LabelSelector>
          <span>{isImageMedia ? 'Image placement' : 'Video placement'}</span>
          <SelectInput
            items={placements}
            value={imagePlacement}
            onChange={updateImagePlacement}
          />
        </LabelSelector>
      ) : null}
      {hasImagePlacement ? (
        <InputBlock label={isImageMedia ? 'Image Position by X' : 'Video Position by X'}>
          <NumberInput
            value={settings.image_position_x}
            onChange={updateImagePositionX}
            type="px"
            min={-1000}
          />
        </InputBlock>
      ) : null}
      {hasImagePlacement ? (
        <InputBlock label={isImageMedia ? 'Image Position by Y' : 'Video Position by Y'}>
          <NumberInput
            value={settings.image_position_y}
            onChange={updateImagePositionY}
            type="px"
            min={-1000}
          />
        </InputBlock>
      ) : null}
      {isImageMedia && <InputBlock label="Opacity">
        <NumberInput
          value={settings.image_opacity}
          onChange={updateOpacity}
          type="%"
          min={0}
          max={100}
        />
      </InputBlock>}
      {isImageMedia && <InputBlock label="Image Border Radius">
        <NumberInput
          value={settings.image_border_radius}
          onChange={(val) => updateSettings({ image_border_radius: val })}
          type="px"
          min={0}
        />
      </InputBlock>}
      {showImageWidth && <InputBlock label="Image Width">
        <NumberInput
          value={settings.image_width}
          onChange={updateImageWidth}
          type="px"
          min={50}
        />
      </InputBlock>}
      {showImageHeight && <InputBlock label="Image Height">
        <NumberInput
          value={settings.image_height}
          onChange={updateImageHeight}
          type="px"
          min={50}
        />
      </InputBlock>}
      {isImageMedia && imagePlacement !== 'position-background' &&
        !(
          imagePlacement === 'position-default' &&
          settings.theme?.image?.position_default === 'background'
        ) && (
          <Fragment>
            <InputBlock label="Overlay color">
              <ColorPicker
                value={settings.image_overlay_color}
                onChange={updateOverlayColor}
              />
            </InputBlock>
            <InputBlock label="Overlay Opacity">
              <NumberInput
                value={settings.image_overlay_opacity}
                onChange={updateOverlayOpacity}
                type="%"
                min={0}
                max={100}
              />
            </InputBlock>
          </Fragment>
        )}
    </CollapsedPanel>
  );
}
